import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, ChefHat, ListOrdered, Search } from "lucide-react";

function csvDownload(rows, filename) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0] || {});
  const csv = [headers.join(",")].concat(
    rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
  ).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const slotPill = (slot) => ({
  breakfast: "bg-amber-50 text-amber-700",
  lunch: "bg-emerald-50 text-emerald-700",
  snacks: "bg-violet-50 text-violet-700",
}[slot] || "bg-cream-200 text-ink-700");

export default function AdminItemSales() {
  const [tab, setTab] = useState("kitchen");
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(plusDays(7));
  const [data, setData] = useState({ kitchen_rows: [], orders: [], total_orders: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [slotFilter, setSlotFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/admin/reports/kitchen-sales?fromDate=${start}&toDate=${end}`)
      .then(r => setData({ kitchen_rows: [], orders: [], total_orders: 0, ...r.data }))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const kitchenFiltered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return data.kitchen_rows.filter(r =>
      (!slotFilter || r.slot === slotFilter)
      && (!q || (r.name || "").toLowerCase().includes(q))
    );
  }, [data.kitchen_rows, searchQuery, slotFilter]);

  const ordersFiltered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return data.orders.filter(o =>
      (!slotFilter || o.slot === slotFilter)
      && (!q ||
        (o.student_name || "").toLowerCase().includes(q)
        || (o.student_id || "").toLowerCase().includes(q)
        || (o.items_summary || "").toLowerCase().includes(q)
      )
    );
  }, [data.orders, searchQuery, slotFilter]);

  const totalQty = kitchenFiltered.reduce((s, r) => s + (r.qty || 0), 0);

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Reports</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Sales — for the kitchen</h1>
            <p className="text-sm text-ink-500 mt-1">List of confirmed orders + per-item totals to plan production.</p>
          </div>
          <div className="inline-flex bg-cream-200 rounded-full p-1">
            <button data-testid="tab-kitchen" onClick={() => setTab("kitchen")} className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 ${tab==="kitchen" ? "bg-ink text-white" : "text-ink-700"}`}>
              <ChefHat size={14} /> Kitchen prep
            </button>
            <button data-testid="tab-orders" onClick={() => setTab("orders")} className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 ${tab==="orders" ? "bg-ink text-white" : "text-ink-700"}`}>
              <ListOrdered size={14} /> Order list
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-border p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">From</label>
            <input data-testid="sales-start" type="date" value={start} onChange={e => setStart(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">To</label>
            <input data-testid="sales-end" type="date" value={end} onChange={e => setEnd(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Slot</label>
            <select data-testid="sales-slot" value={slotFilter} onChange={e => setSlotFilter(e.target.value)}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm bg-white">
              <option value="">All slots</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="snacks">Snacks</option>
            </select>
          </div>
          <div className="col-span-1 md:col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
              <input data-testid="sales-search" type="text"
                placeholder={tab === "kitchen" ? "Item name…" : "Student, item, order ID…"}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="block w-full rounded-xl border border-border pl-9 pr-3 py-2 text-sm focus:border-brand focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Orders in range</div>
            <div className="font-heading text-3xl font-extrabold mt-2" data-testid="stat-orders">{data.total_orders}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Items to prepare</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-emerald-700" data-testid="stat-items">{totalQty}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Unique items</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-violet-700">{kitchenFiltered.length}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border flex items-center justify-end">
            <button data-testid="sales-csv"
              onClick={() => csvDownload(tab === "kitchen" ? kitchenFiltered : ordersFiltered, `sales-${tab}-${start}_to_${end}.csv`)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink text-white text-sm font-semibold hover:bg-ink-700">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Tab content */}
        {tab === "kitchen" && (
          <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid="kitchen-table">
            <table className="w-full text-sm">
              <thead className="bg-cream-200">
                <tr>
                  <th className="p-3 text-left text-xs uppercase font-bold">Item</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Slot</th>
                  <th className="p-3 text-right text-xs uppercase font-bold">Qty to prepare</th>
                  <th className="p-3 text-right text-xs uppercase font-bold">Order count</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="p-8 text-center text-ink-500">Loading…</td></tr>}
                {!loading && kitchenFiltered.length === 0 && (
                  <tr><td colSpan={4} className="p-10 text-center text-ink-500">No items in the selected range / filters.</td></tr>
                )}
                {!loading && kitchenFiltered.sort((a, b) => (b.qty || 0) - (a.qty || 0)).map((r, i) => (
                  <tr key={`${r.name}-${r.slot}-${i}`} className="border-t border-border" data-testid={`kitchen-row-${i}`}>
                    <td className="p-3 font-semibold">{r.name}</td>
                    <td className="p-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold capitalize ${slotPill(r.slot)}`}>{r.slot}</span>
                    </td>
                    <td className="p-3 text-right font-bold text-emerald-700">{r.qty}</td>
                    <td className="p-3 text-right text-ink-500">{r.orders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="bg-white rounded-xl border border-border overflow-x-auto" data-testid="orders-table">
            <table className="w-full text-sm">
              <thead className="bg-cream-200">
                <tr>
                  <th className="p-3 text-left text-xs uppercase font-bold">Date</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Slot</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Student</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Student ID</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Grade</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Items</th>
                  <th className="p-3 text-right text-xs uppercase font-bold">Qty</th>
                  <th className="p-3 text-left text-xs uppercase font-bold">Type</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="p-8 text-center text-ink-500">Loading…</td></tr>}
                {!loading && ordersFiltered.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-ink-500">No orders match the filters.</td></tr>
                )}
                {!loading && ordersFiltered.map((o, i) => (
                  <tr key={o.order_id || i} className="border-t border-border" data-testid={`order-row-${i}`}>
                    <td className="p-3 font-semibold">{o.scheduled_date}</td>
                    <td className="p-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold capitalize ${slotPill(o.slot)}`}>{o.slot}</span>
                    </td>
                    <td className="p-3">{o.student_name}</td>
                    <td className="p-3 font-mono text-xs text-ink-500">{o.student_id || "—"}</td>
                    <td className="p-3 text-ink-500">{o.grade || "—"}</td>
                    <td className="p-3 text-ink-700 max-w-[360px] truncate" title={o.items_summary}>{o.items_summary}</td>
                    <td className="p-3 text-right font-bold">{o.items_count}</td>
                    <td className="p-3"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-cream-200">{o.type || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
